import React, { useEffect } from "react";

import {
    Control,
    UseFormHandleSubmit,
    UseFormResetField,
    UseFormSetValue,
    useWatch,
} from "react-hook-form";

import shallow from "zustand/shallow";

import ReactHookInput from "../../../../shared/forms/ReactHookInput";
import ReactHookSelect from "../../../../shared/forms/ReactHookSelect";
import NewHeadline from "../../../../shared/NewHeadline";
import AddressPicker from "../../components/AddressPicker";
import Checkbox, { CheckboxContainer } from "../../components/Checkbox";
import useCheckoutStore, { iCheckoutForm } from "./../store";

interface iStepDeliveryForm {
    children: React.ReactNode;
    onSubmit(data: iCheckoutForm, event?: React.BaseSyntheticEvent): void;
    control: Control<iCheckoutForm>;
    handleSubmit: UseFormHandleSubmit<iCheckoutForm>;
    resetField: UseFormResetField<iCheckoutForm>;
    setValue: UseFormSetValue<iCheckoutForm>;
}

const StepDeliveryForm = ({
    children,
    onSubmit,
    control,
    handleSubmit,
    resetField,
}: iStepDeliveryForm) => {
    const { salutations, fetchSalutations } = useCheckoutStore(
        (state) => ({
            form: state.form,
            salutations: state.salutations,
            fetchSalutations: state.fetchSalutations,
        }),
        shallow
    );

    const hasShippingAddress = useWatch({
        control,
        name: "hasShippingAddress",
    });

    useEffect(() => {
        if (salutations.length < 1) {
            fetchSalutations();
        }
    }, []);

    useEffect(() => {
        if (hasShippingAddress === false) {
            resetField("shippingAddress");
        }
    }, [hasShippingAddress]);

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <NewHeadline>Kontaktdaten</NewHeadline>
                <ReactHookInput
                    name="billingAddress.company"
                    placeholder="Firma (optional)"
                    rules={{ required: false }}
                    control={control}
                    ifNullish=""
                />

                <ReactHookInput
                    name="vatId"
                    placeholder="USt-IdNr. (optional)"
                    rules={{ required: false }}
                    control={control}
                    ifNullish=""
                />

                <ReactHookSelect
                    name="salutationId"
                    placeholder="Anrede"
                    control={control}
                    rules={{ required: true }}
                    options={salutations.map((salutation) => ({
                        text: salutation.displayName,
                        value: salutation.id,
                    }))}
                />

                <ReactHookInput
                    name="firstName"
                    placeholder="Vorname"
                    rules={{ required: true }}
                    control={control}
                />

                <ReactHookInput
                    name="lastName"
                    placeholder="Nachname"
                    rules={{ required: true }}
                    control={control}
                />

                <ReactHookInput
                    name="email"
                    placeholder="E-Mail"
                    rules={{ required: true }}
                    control={control}
                />

                <ReactHookInput
                    name="billingAddress.phoneNumber"
                    placeholder="Telefon"
                    rules={{ required: true }}
                    control={control}
                />

                <NewHeadline>Lieferdetails</NewHeadline>
                <AddressPicker
                    zipcodeField="billingAddress.zipcode"
                    cityField="billingAddress.city"
                    streetField="billingAddress.street"
                    houseNumberField="billingAddress.houseNumber"
                    countryField="billingAddress.countryId"
                    control={control}
                />

                <CheckboxContainer>
                    <Checkbox name="hasShippingAddress" control={control}>
                        Die Rechnungsadresse weicht von der Lieferadresse ab.
                    </Checkbox>
                </CheckboxContainer>

                {hasShippingAddress && (
                    <>
                        <NewHeadline>Abweichende Lieferadresse</NewHeadline>
                        <ReactHookSelect
                            name="shippingAddress.salutationId"
                            placeholder="Anrede"
                            control={control}
                            rules={{ required: true }}
                            options={salutations.map((salutation) => ({
                                text: salutation.displayName,
                                value: salutation.id,
                            }))}
                        />

                        <ReactHookInput
                            name="shippingAddress.firstName"
                            placeholder="Vorname"
                            rules={{ required: true }}
                            control={control}
                        />

                        <ReactHookInput
                            name="shippingAddress.lastName"
                            placeholder="Nachname"
                            rules={{ required: true }}
                            control={control}
                        />

                        <AddressPicker
                            zipcodeField="shippingAddress.zipcode"
                            cityField="shippingAddress.city"
                            streetField="shippingAddress.street"
                            houseNumberField="shippingAddress.houseNumber"
                            countryField="shippingAddress.countryId"
                            control={control}
                        />
                    </>
                )}
                {children}
            </form>
        </>
    );
};

export default StepDeliveryForm;
